import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
	type Control,
	type FieldValues,
	type Path,
	useWatch
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { RequiredChip } from '@/components/Chips/RequiredChip';

import { DcrFormController } from '../../DcrFormController/DcrFormController';

import {
	MAX_CHARS,
	reviewModalNotesStyles as sx
} from './ReviewModalNotes.styles';

type Props<T extends FieldValues> = {
	control: Control<T>;
	required?: boolean;
	expandOnFocus?: boolean;
};

export const ReviewModalNotes = <T extends FieldValues>({
	control,
	required = false,
	expandOnFocus = false
}: Props<T>) => {
	const { t } = useTranslation();
	const [isFocused, setIsFocused] = useState(false);
	const inputRef = useRef<HTMLTextAreaElement | null>(null);

	const notesValue = useWatch({ control, name: 'notes' as Path<T> });
	const collapsed = expandOnFocus && !isFocused;
	const minRows = collapsed ? 1 : 3;
	const maxRows = collapsed ? 1 : 10;
	const [overflows, setOverflows] = useState(false);

	const measureOverflow = useCallback(() => {
		const el = inputRef.current;
		if (!el) return;

		const lineHeight = parseFloat(window.getComputedStyle(el).lineHeight) || 0;
		const isMultiLine =
			el.scrollHeight > Math.max(el.clientHeight, lineHeight) + 1;

		setOverflows(prev => (prev === isMultiLine ? prev : isMultiLine));
	}, []);

	useEffect(() => {
		measureOverflow();
	}, [notesValue, collapsed, measureOverflow]);

	useEffect(() => {
		const el = inputRef.current;
		if (!el || typeof ResizeObserver === 'undefined') return;

		const observer = new ResizeObserver(measureOverflow);
		observer.observe(el);

		return () => observer.disconnect();
	}, [measureOverflow]);

	return (
		<Box
			sx={sx.root}
			onMouseDown={e => {
				// Keep textarea focus when clicking padding inside root
				if (e.target !== inputRef.current) e.preventDefault();
			}}
			onClick={() => inputRef.current?.focus()}
		>
			<DcrFormController
				name={'notes' as Path<T>}
				control={control}
				filterable={false}
				render={({ field }) => (
					<>
						{required && !field.value && (
							<RequiredChip
								customSx={{
									root: [sx.requiredChip, { fontWeight: 400 }]
								}}
							/>
						)}

						<Box sx={sx.fieldWrapper}>
							<TextField
								{...field}
								inputRef={inputRef}
								placeholder={t('Write a note to us.')}
								multiline
								minRows={minRows}
								maxRows={maxRows}
								variant='standard'
								fullWidth
								onFocus={() => setIsFocused(true)}
								onBlur={e => {
									field.onBlur();
									setIsFocused(false);
									e.preventDefault();
								}}
								slotProps={{
									input: { disableUnderline: true },
									htmlInput: {
										maxLength: MAX_CHARS,
										sx: collapsed
											? { ...sx.notesText, ...sx.notesTextCollapsed }
											: sx.notesText
									}
								}}
							/>
							{collapsed && overflows && <Box sx={sx.fadeOverlay} />}
						</Box>
					</>
				)}
			/>
		</Box>
	);
};
